// @owners { team: patient-health }
import { COLORS } from '@alto/design-library-tokens';
import { ActionSheetContext, Description, FlatCard, LgPadding, XsSpacing } from '@alto/design-system';
// eslint-disable-next-line @alto/no-pocky-import
import { Button, Link, Row } from '@alto/pocky';
import React, { useCallback, useContext, useEffect } from 'react';
import { openModal } from '~shared/actions/modal';
import {
  formatNameWithCommas,
  getDoctorNameOrEmptyString,
  getOriginalMedNameOrEmptyString,
} from '~shared/features/my-meds/helper';
import { type Medication } from '~shared/features/my-meds/types';
import { useAnalytics } from '~shared/hooks';
import { InfoboxFlowType, useMedInfoboxFlows } from '~shared/hooks/useMedInfoboxFlows';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared } from '~shared/store';
import { GetItSoonerActionSheet } from '~web/features/my-meds/components/modals/GetItSoonerActionSheet';

type ExtraDetailsLinkProps = {
  readonly extraDetailsLinkText: string | undefined;
  readonly flowInfoType: string;
  readonly medication: Medication;
};

const ExtraDetailsLink = ({ extraDetailsLinkText, flowInfoType, medication }: ExtraDetailsLinkProps) => {
  const dispatch = useDispatchShared();
  const { trackPageView } = useAnalytics();
  const { setActiveActionSheet, closeActionSheet } = useContext(ActionSheetContext);
  const prescriptionId = medication.representativePrescription.id;

  useEffect(() => {
    trackPageView({
      event: EVENTS.MED_DETAILS__INFOBOX_VIEWED,
      additionalFields: { prescriptionId },
      params: { prescriptionId, flow: flowInfoType },
    });
  }, [flowInfoType, prescriptionId, trackPageView]);

  const prescription = medication.representativePrescription;
  const providerName = getDoctorNameOrEmptyString(prescription?.doctor);
  const renderModal = useCallback(() => {
    switch (flowInfoType) {
      case InfoboxFlowType.PRIOR_AUTH:
        dispatch(openModal('PRIOR_AUTH_MODAL'));
        return;
      case InfoboxFlowType.CLARIFICATION:
        dispatch(openModal('CLARIFICATION_MODAL', { providerName }));
        return;
      case InfoboxFlowType.REFILL_TOO_SOON:
        setActiveActionSheet(
          <GetItSoonerActionSheet
            prescription={prescription}
            onClose={closeActionSheet}
            origin={ORIGIN_NAMES.MED_DETAILS}
          />,
        );
        return;
      case InfoboxFlowType.ALTERNATIVE_MED:
        dispatch(
          openModal('ALTERNATIVE_MEDICATION_MODAL', {
            formattedProviderName: formatNameWithCommas(providerName),
            originalMedName: getOriginalMedNameOrEmptyString(prescription),
          }),
        );
        return;
      case InfoboxFlowType.EARLY_ORDER_ELIGIBLE:
      case InfoboxFlowType.EARLY_ORDER_REQUESTED:
      case InfoboxFlowType.PROGYNY_EXPIRED_COVERAGE:
        // intentionally unavailable on web
        return;
      default:
        // technically this will never get hit since this infobox only renders if useMedInfoboxFlows returns a valid InfoboxFlowType
        return;
    }
  }, [closeActionSheet, dispatch, flowInfoType, prescription, providerName, setActiveActionSheet]);

  if (!extraDetailsLinkText) return null;

  if (flowInfoType === InfoboxFlowType.REFILL_TOO_SOON) {
    return (
      <Row>
        <Button
          small
          kind="secondary"
          onClick={renderModal}
        >
          {extraDetailsLinkText}
        </Button>
      </Row>
    );
  }

  return <Link onClick={renderModal}>{extraDetailsLinkText}</Link>;
};

export type Props = {
  readonly medication: Medication;
};

export const Infobox = ({ medication }: Props) => {
  const flowInfo = useMedInfoboxFlows(medication);

  if (!flowInfo) {
    return null;
  }

  return (
    <>
      <XsSpacing />
      <FlatCard>
        <LgPadding>
          <Description
            fontFamily="semibold"
            color={COLORS.TEXT_COLORS.PRIMARY}
          >
            {flowInfo.title}
          </Description>
          <XsSpacing />
          <Description color={COLORS.TEXT_COLORS.GREY}>{flowInfo.message}</Description>
          <XsSpacing />
          <ExtraDetailsLink
            extraDetailsLinkText={flowInfo.extraDetailsLinkText}
            flowInfoType={flowInfo.type}
            medication={medication}
          />
        </LgPadding>
      </FlatCard>
      <XsSpacing />
    </>
  );
};
